'use strict';

import React, { useCallback } from 'react';
import { Box, Link as MuiLink } from '@mui/material';
import { Link, useHistory } from 'react-router-dom';
import { Brand } from '@/lenses/uilenses/theme/colors';
import { useIsAuthenticated } from '@/hooks/useIsAuthenticated.js';
import StudyBuilderPage from './StudyBuilderPage.jsx';
import { useStudyBuilderAuthSession } from './useStudyBuilderAuthSession.js';
import { readStudyBuilderGuestSession, writeStudyBuilderGuestSession } from './studyBuilderGuestSession.js';

const BUILD_A_STUDY_PATH = '/home/build-a-study';

/**
 * Study builder reachable without portal login. Auth is required only when revealing the estimate (handled in QuoteWizard).
 * Guests can Sign in from the header; authed users can open My projects.
 */
export function PublicStudyBuilderLayout() {
	useStudyBuilderAuthSession();
	const history = useHistory();
	const authenticated = useIsAuthenticated();
	const exitHomePath = authenticated ? '/home' : '/';

	const handleGuestSignIn = useCallback(() => {
		const existing = readStudyBuilderGuestSession();
		const hasQuoteDraft = Boolean(
			existing?.quote
			&& (Array.isArray(existing.quote.lineItems) || existing.quote.sampleInfo)
		);
		if (hasQuoteDraft || existing?.phase === 'build') {
			writeStudyBuilderGuestSession({
				phase: existing?.phase || 'build',
				revealEstimateAfterLogin: true,
			});
		}
		const returnPath = `${window.location.origin}${BUILD_A_STUDY_PATH}`;
		localStorage.setItem('returnUrl', returnPath);
		history.push(`/login?return_url=${encodeURIComponent(returnPath)}`);
	}, [history]);

	return (
		<Box sx={{ minHeight: '100vh', background: Brand.metaboliteWhite }}>
			<Box
				sx={{
					px: 2,
					py: 1.5,
					display: 'flex',
					justifyContent: 'flex-end',
					alignItems: 'center',
					gap: 2,
					borderBottom: `1px solid ${Brand.precisionGrey}`,
					background: Brand.pureWhite,
				}}
			>
				{authenticated ? (
					<MuiLink
						component={Link}
						to="/home"
						data-testid="study-builder-my-projects"
						sx={{ fontWeight: 600 }}
					>
						My projects
					</MuiLink>
				) : (
					<MuiLink
						component="button"
						type="button"
						onClick={handleGuestSignIn}
						data-testid="study-builder-sign-in"
						sx={{
							fontWeight: 600,
							border: 'none',
							background: 'none',
							cursor: 'pointer',
							font: 'inherit',
							color: 'inherit',
							padding: 0,
						}}
					>
						Sign in
					</MuiLink>
				)}
			</Box>
			<StudyBuilderPage exitHomePath={exitHomePath} enableGuestSessionPersistence />
		</Box>
	);
}
